import dayjs from 'dayjs';
import { useAtomValue } from 'jotai';
import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router';

import SmallCard from '@/components/SmallCard';
import { YEAR_MONTH_DAY_FORMAT } from '@/constants/date.constants';
import AvgAckTimeWidget from '@/features/AvgAckTimeWidget';
import { getMetricTimes } from '@/pages/ReportsTemplates/MDR/ReportsPages/TicketsPages/TicketsPages.utils';
import type { TicketMetricsByPriority } from '@/pages/ReportsTemplates/ReportsTemplate.types';

import { useFetchStatusTimes } from '../../Dashboard.hooks';
import dashboardTimeFrameAtom from '../../Dashboard.store';
import useStyles from '../../Dashboard.styles';
import type { TicketsAckTimesDTO } from '../../Dashboard.types';
import { widgetSearchParams } from '../../Dashboard.utils';
import type { SummarySmallCardsProps } from './SummarySmallCards.types';

const SummarySmallCards = (props: SummarySmallCardsProps) => {
  const {
    openTicketsCount,
    highPriorityTicketsCount,
    awaitingRemediationTicketsCount,
    selectedOrganization,
    timeFilter
  } = props;

  const navigate = useNavigate();
  const { classes } = useStyles();
  const dashboardTimeFrame = useAtomValue(dashboardTimeFrameAtom);

  const { data: allMetrics } = useFetchStatusTimes(
    dayjs().subtract(dashboardTimeFrame, 'day').format(YEAR_MONTH_DAY_FORMAT),
    dayjs().add(1, 'day').format(YEAR_MONTH_DAY_FORMAT) // using current date was causing an issue with the test query, likely due to timezones
  );

  const { mttdMetrics, mttaMetrics, mttnMetrics } = useMemo(
    () => ({
      mttdMetrics: getMetricTimes(
        'mttd',
        allMetrics as TicketMetricsByPriority[]
      ),
      mttaMetrics: getMetricTimes(
        'mtta',
        allMetrics as TicketMetricsByPriority[]
      ),
      mttnMetrics: getMetricTimes(
        'mttn',
        allMetrics as TicketMetricsByPriority[]
      )
    }),
    [allMetrics]
  );

  const handleCardClick = useCallback(
    (title: string) => {
      navigate({
        pathname: '/tickets',
        search: widgetSearchParams(title, timeFilter).toString()
      });
    },
    [timeFilter]
  );

  return (
    <>
      <div style={{ cursor: 'pointer' }} className={classes.dashboardSmallItem}>
        <SmallCard
          onCardClick={handleCardClick}
          color="error"
          title="High Priority Tickets"
          primaryContent={highPriorityTicketsCount}
          tooltipText="All incomplete P1 and P2 tickets"
        />
      </div>
      <div style={{ cursor: 'pointer' }} className={classes.dashboardSmallItem}>
        <SmallCard
          color="warning"
          title="Open Tickets"
          primaryContent={openTicketsCount}
          tooltipText="Need Acknowledgement"
          onCardClick={handleCardClick}
        />
      </div>
      <div style={{ cursor: 'pointer' }} className={classes.dashboardSmallItem}>
        <SmallCard
          onCardClick={handleCardClick}
          title="Unresolved Tickets"
          primaryContent={awaitingRemediationTicketsCount}
          tooltipText="All Open and Acknowledged tickets"
        />
      </div>
      <div className={classes.dashboardSmallItem}>
        <SmallCard
          title="Avg. Detection Time"
          primaryContent={
            selectedOrganization.id > 0 ? (
              <AvgAckTimeWidget
                showAllPriorities={false}
                allPrioritiesAckTimes={mttdMetrics as TicketsAckTimesDTO[]}
              />
            ) : (
              <div style={{ fontSize: '1rem' }}>Select an organization</div>
            )
          }
          tooltipText="MTTD"
        />
      </div>
      <div className={classes.dashboardSmallItem}>
        <SmallCard
          title="Avg. Escalation Time"
          primaryContent={
            selectedOrganization.id > 0 ? (
              <AvgAckTimeWidget
                showAllPriorities={false}
                allPrioritiesAckTimes={mttnMetrics as TicketsAckTimesDTO[]}
              />
            ) : (
              <div style={{ fontSize: '1rem' }}>Select an organization</div>
            )
          }
          tooltipText="MTTN"
        />
      </div>
      <div className={classes.dashboardSmallItem}>
        <SmallCard
          title="Avg. Acknowledgement Time"
          primaryContent={
            selectedOrganization.id > 0 ? (
              <AvgAckTimeWidget
                showAllPriorities={false}
                allPrioritiesAckTimes={mttaMetrics as TicketsAckTimesDTO[]}
              />
            ) : (
              <div style={{ fontSize: '1rem' }}>Select an organization</div>
            )
          }
          tooltipText="MTTA"
        />
      </div>
    </>
  );
};

SummarySmallCards.displayName = 'SummarySmallCards';

export default SummarySmallCards;
